import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { closeMyAccount, getMyAccount, reopenMyAccount } from "../api/apiClient";
import { logout } from "../api/session";

/**
 * Account.
 *
 * Closing an account is deliberately a two-step, reversible process: the
 * request stops future renewals and starts a grace period, and nothing is
 * deleted until that runs out.
 */

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

function Field({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={500}>{value || "—"}</Typography>
    </Box>
  );
}

export default function AccountPage() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAccount(await getMyAccount());
    } catch (err) {
      console.error("Failed to load account", err);
      setError("Could not load your account.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleClose = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await closeMyAccount(confirmText.trim());
      setCloseOpen(false);
      setConfirmText("");
      setNotice(result.message);
      await load();
    } catch (err) {
      console.error("Close failed", err);
      setError(err.response?.data?.detail || "Could not close your account.");
    } finally {
      setBusy(false);
    }
  };

  const handleReopen = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await reopenMyAccount();
      setNotice(result.message);
      await load();
    } catch (err) {
      console.error("Reopen failed", err);
      setError(err.response?.data?.detail || "Could not reopen your account.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const pendingClosure = account?.account_status === "PENDING_CLOSURE";

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", px: { xs: 0.5, sm: 1 }, py: 2 }}>
      <Typography variant="h4" fontWeight={800} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <PersonOutlineIcon fontSize="large" /> Account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Your details and account settings.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {pendingClosure && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleReopen} disabled={busy}>
              Keep my account
            </Button>
          }
        >
          <AlertTitle>Your account is scheduled to close</AlertTitle>
          It closes on <strong>{formatDate(account.closes_at)}</strong>
          {account.days_left_in_grace !== null && ` — ${account.days_left_in_grace} days from now`}.
          Your projects are still here and nothing has been deleted. Future subscription renewals
          have already been stopped.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Your details
        </Typography>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}><Field label="Name" value={account.name} /></Grid>
          <Grid item xs={12} sm={6}><Field label="Email" value={account.email} /></Grid>
          <Grid item xs={12} sm={6}><Field label="Phone" value={account.phone_number} /></Grid>
          <Grid item xs={12} sm={6}><Field label="Member since" value={formatDate(account.member_since)} /></Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="h6" fontWeight={700}>Plan &amp; credits</Typography>
          <Button component={Link} to="/build/billing" size="small">Go to Billing</Button>
        </Stack>
        <Grid container spacing={2.5}>
          <Grid item xs={6} sm={4}>
            <Field label="Plan" value={account.subscription?.plan_name || "No subscription"} />
          </Grid>
          <Grid item xs={6} sm={4}><Field label="Build credits" value={account.build_credits} /></Grid>
          <Grid item xs={6} sm={4}>
            <Field label="Free blueprints" value={account.free_blueprint_attempts} />
          </Grid>
        </Grid>
        {account.subscription?.cancel_at_period_end && (
          <Chip
            size="small"
            color="warning"
            sx={{ mt: 2 }}
            label={`Subscription ends ${formatDate(account.subscription.current_period_end)}`}
          />
        )}
      </Paper>

      {/* --- Closing the account --- */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: "error.light" }}>
        <Typography variant="h6" fontWeight={700} color="error.main">
          Close account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          Closing your account stops any future subscription renewals straight away. You then have{" "}
          <strong>{account.grace_days} days</strong> to change your mind — nothing is deleted before
          that. After the grace period your projects and personal details are removed. Billing and
          transaction records are kept, because we are required to retain them.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {pendingClosure ? (
          <Button variant="contained" onClick={handleReopen} disabled={busy}>
            Keep my account open
          </Button>
        ) : (
          <Button variant="outlined" color="error" onClick={() => setCloseOpen(true)} disabled={busy}>
            Close my account
          </Button>
        )}
      </Paper>

      <Dialog open={closeOpen} onClose={() => setCloseOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Close your account?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This stops future subscription renewals now. Your account and projects stay available
            for <strong>{account.grace_days} days</strong> in case you change your mind, then they
            are permanently removed.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            Type <strong>CLOSE</strong> to confirm.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Type CLOSE"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseOpen(false)}>Keep my account</Button>
          <Button
            onClick={handleClose}
            color="error"
            variant="contained"
            disabled={busy || confirmText.trim().toUpperCase() !== "CLOSE"}
          >
            Close account
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 3, textAlign: "right" }}>
        <Button
          variant="text"
          onClick={() => {
            logout({ redirect: false });
            navigate("/build/login", { replace: true });
          }}
        >
          Sign out
        </Button>
      </Box>
    </Box>
  );
}
